import { styled } from '@mui/material';

import { neutral } from '@/styles/constants/colors';
import { fontFamily, fontWeight } from '@/styles/constants/fonts';

// ── Capture step ───────────────────────────────────────────

export const ScannerContainer = styled('div')`
  width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: #000;
  font-family: ${fontFamily.primary};
`;

export const CameraSection = styled('div')`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: hidden;
  background: #000;
`;

export const StyledVideo = styled('video')`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

export const StyledOverlay = styled('canvas')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const CaptureActions = styled('div')`
  position: absolute;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  padding: 0 16px;
  display: flex;
  justify-content: center;
  z-index: 2;
`;

export const CaptureNowButton = styled('button')`
  box-sizing: border-box;
  min-width: 180px;
  min-height: 48px;
  padding: 12px 32px;
  border: none;
  border-radius: 28px;
  font-size: 1rem;
  font-weight: ${fontWeight.semiBold};
  font-family: ${fontFamily.primary};
  cursor: pointer;
  color: #fff;
  background: ${({ theme }) => theme.palette.red[500]};
  box-shadow: 0 4px 16px rgba(214, 50, 48, 0.4);
  transition:
    transform 0.15s,
    background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.palette.red[400]};
  }
  &:active {
    transform: scale(0.97);
  }
`;

// ── Camera status overlay ──────────────────────────────────

export const StatusOverlay = styled('div')<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3;
  background: ${neutral.max};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s ease-out;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

export const BackButton = styled('button')`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 4;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: ${fontWeight.medium};
  font-family: ${fontFamily.primary};
  cursor: pointer;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

export const StatusContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  position: relative;
`;

export const StatusGuideFrame = styled('div')`
  width: 70%;
  max-width: 600px;
  aspect-ratio: 1.586;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  /* Visual match to canvas stroke */
  border-radius: 16px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.45);

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  @media (orientation: portrait) {
    aspect-ratio: ${1 / 1.586};
  }
`;

export const StatusIndicator = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  color: rgba(255, 255, 255, 0.7);
`;

export const StatusMessage = styled('p')`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  font-weight: 500;
  font-family: Poppins, system-ui, sans-serif;
  text-align: center;
  line-height: 1.6;
  width: 80%;
  max-width: 480px;
`;

// ── Review step ────────────────────────────────────────────

export const ReviewPage = styled('div')`
  width: 100%;
  height: 100vh;
  height: 100dvh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: max(${({ theme }) => theme.spacing(2)}, env(safe-area-inset-top))
    max(${({ theme }) => theme.spacing(2)}, env(safe-area-inset-right))
    max(${({ theme }) => theme.spacing(2)}, env(safe-area-inset-bottom))
    max(${({ theme }) => theme.spacing(2)}, env(safe-area-inset-left));
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: ${({ theme }) => theme.palette.neutral[100]};
  font-family: ${fontFamily.primary};
  box-sizing: border-box;
`;

export const ReviewBackButton = styled('button')`
  position: absolute;
  top: max(${({ theme }) => theme.spacing(2)}, env(safe-area-inset-top));
  left: max(${({ theme }) => theme.spacing(2)}, env(safe-area-inset-left));
  z-index: 1;
  flex: 0 0 auto;
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  min-height: 44px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: ${fontWeight.medium};
  font-family: ${fontFamily.primary};
  cursor: pointer;
  color: ${neutral[1000]};
  background: ${neutral.min};
  border: 1px solid ${neutral[300]};
  transition: background 0.2s;

  &:hover {
    background: ${neutral[100]};
  }

  @media (max-height: 600px) {
    position: static;
  }
`;

export const ReviewContainer = styled('div')`
  flex: 0 0 auto;
  margin-block: auto;
  width: 100%;
  max-width: 540px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.palette.neutral.min};
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.palette.neutral[200]};
  padding: clamp(
    ${({ theme }) => theme.spacing(2)},
    4vw,
    ${({ theme }) => theme.spacing(4)}
  );
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(
    ${({ theme }) => theme.spacing(2)},
    3vh,
    ${({ theme }) => theme.spacing(3)}
  );

  @media (orientation: landscape) and (max-height: 600px) {
    padding: ${({ theme }) => theme.spacing(1.5, 2)};
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const ReviewTitle = styled('h2')`
  font-size: 1.25rem;
  font-weight: ${fontWeight.semiBold};
  font-family: ${fontFamily.primary};
  color: ${({ theme }) => theme.palette.text.primary};
  text-align: center;
  margin: 0;
`;

export const ReviewImageWrapper = styled('div')`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.neutral[100]};

  @media (orientation: landscape) and (max-height: 600px) {
    max-height: 34dvh;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const ReviewImage = styled('img')`
  display: block;
  width: 100%;
  height: auto;

  @media (orientation: landscape) and (max-height: 600px) {
    width: auto;
    max-width: 100%;
    max-height: 34dvh;
    object-fit: contain;
  }
`;

export const ReviewActions = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

export const RetakeButton = styled('button')`
  flex: 1;
  min-height: 48px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: ${fontWeight.semiBold};
  font-family: ${fontFamily.primary};
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.primary};
  background: ${({ theme }) => theme.palette.neutral.min};
  border: 1px solid ${({ theme }) => theme.palette.neutral[300]};
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.palette.neutral[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ConfirmButton = styled('button')`
  flex: 1;
  min-height: 48px;
  padding: 12px 16px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: ${fontWeight.semiBold};
  font-family: ${fontFamily.primary};
  cursor: pointer;
  color: #fff;
  background: ${({ theme }) => theme.palette.primaries[500]};
  transition:
    transform 0.15s,
    background 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.palette.primaries[400]};
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
